import { Injectable } from '@angular/core'
import { HttpClient, HttpParams } from '@angular/common/http'

import { environment } from '../../environments/environment'
import { Article, Technician, UserEmailHash, Warehouse } from '../_models'
import {
  APTechnicianHash,
  TechnicianUpdateHash,
  TechnicianWarehouseHash,
  WPTechnician
} from "../_models/technician.interface";
import { WarehouseWithProjectBins } from "../_models/warehouse.interface";

@Injectable({
  providedIn: 'root'
})
export class UserService {

  constructor(
      private http: HttpClient,
    ) {
  }

  public getTechnicians(params: any = {}) {
    const path = environment.apiURL + 'technicians.json'
    return this.http.get<Technician[]>(path, { params: params })
  }

  public createUser(create_params: TechnicianUpdateHash, send_account?: boolean) {
    const path = environment.apiURL + `users/create` + (send_account ? '?send_account=true' : '')
    return this.http.post<APTechnicianHash>(path, create_params)
  }

  public updateUser(id: number, update_params: TechnicianUpdateHash, send_account?: boolean) {
    const path = environment.apiURL + `users/${id}` + (send_account ? '?send_account=true' : '')
    return this.http.patch<APTechnicianHash>(path, update_params)
  }

  public deleteUser(id: number) {
    const path = environment.apiURL + `users/${id}`
    return this.http.delete<APTechnicianHash>(path)
  }

  public userIsDeletable(id: number) {
    const path = environment.apiURL + `users/${id}/deletable`
    return this.http.get<{deletable: boolean}>(path)
  }

  public updateUserRoles(id: number, add_roles_with_ids: number[], delete_roles_with_ids: number[]) {
    const path = environment.apiURL + `users/${id}/update_roles`

    const data = {
      roles: {
        add_roles_with_ids,
        delete_roles_with_ids
      }
    }

    return this.http.post<APTechnicianHash>(path, data)
  }

  placeHolderTechnicians() {
    const path = environment.apiURL + 'technicians/placeholder'

    return this.http.get<Technician[]>(path)
  }

  // Get Admin Page Hashes
  public getUsersAPHashes() {
    const path = environment.apiURL + 'users/all'

    let params = new HttpParams()
    params = params.append('hash_type', 'admin_page_hash')

    return this.http.get<APTechnicianHash[]>(path, {params})
  }

  // Get Welcome Page Hashes
  public getUsersWPHashes(for_date?: string) {
    const path = environment.apiURL + 'users/all'

    let params = new HttpParams()
    params = params.append('hash_type', 'welcome_page_hash')
    if (for_date) params = params.append('for_date', for_date)

    return this.http.get<WPTechnician[]>(path, {params})
  }

  // Get Warehouse Hashes
  public getUsersWarehouseHashes() {
    const path = environment.apiURL + 'users/all'

    let params = new HttpParams()
    params = params.append('hash_type', 'warehouses_hash')

    return this.http.get<TechnicianWarehouseHash[]>(path, {params})
  }


  // Get Admin Page User
  public getAPUser(id: number) {
    const path = environment.apiURL + `users/${id}`

    let params = new HttpParams()
    params = params.append('hash_type', 'admin_page_hash')

    return this.http.get<APTechnicianHash>(path, {params})
  }

  public getUsersWarehouses(id: number | string) {
    const path = environment.apiURL + `users/${id}/warehouses`

    return this.http.get<Warehouse[]>(path)
  }

  public getWarehousesWithProjectBins(uid: number) {
    const path = environment.apiURL + `users/${uid}/warehouses?hash_type=active_project_bins_hash`
    return this.http.get<WarehouseWithProjectBins[]>(path)
  }

  getSubcontractors() {
    const path = environment.apiURL + `subcontractors`
    return this.http.get<Technician[]>(path)
  }

  getDispatcher() {
    const path = environment.apiURL + `dispatcher`
    return this.http.get<Technician[]>(path)
  }

  public getProjectTechnicians(projectId: number) {
    const path = environment.apiURL + 'project/' + projectId + '/technicians.json'
    return this.http.get<Technician[]>(path)
  }

  public priorityBasedOperationTimes(uid: number, priority_id: number) {
    let params = new HttpParams()

    params = params.append('pid', priority_id.toString())

    const path = environment.apiURL + 'users/' + uid + '/priority_based_operation_times'

    return this.http.get<{operation_id: number, duration: number, date: string}[]>(path, {params})
  }

  public priority_operation_count(uid: number, priority_id: number) {
    let params = new HttpParams()

    params = params.append('priority_id', priority_id.toString())

    const path = environment.apiURL + 'users/' + uid + '/priority_operation_count'

    return this.http.get<{user_id: number, priority_id: number, done_priority_operations_count: number}>(path, {params})
  }

  public project_operation_count(uid: number, project_id: number) {
    let params = new HttpParams()

    params = params.append('project_id', project_id.toString())

    const path = environment.apiURL + 'users/' + uid + '/project_operation_count'

    return this.http.get<{user_id: number, project_id: number, done_project_operations_count: number}>(path, {params})
  }

  public distancesToCoordinates(uids: number[], lat: number, lng: number) {

    let params = new HttpParams()

    for (let uid of uids) {
      params = params.append('ids[]', uid.toString())
    }

    params = params.append('lat', lat.toString())
    params = params.append('lng', lng.toString())

    const path = environment.apiURL + 'users/distance_to_coordinates'

    return this.http.get<{id: number, distance: number, duration: number, valid: boolean}[]>(path, {params})
  }

  public getArticles(uid: number) {
    const path = environment.apiURL + `users/${uid}/articles`
    return this.http.get<Article[]>(path)
  }

  public getUserIdsWithOperationsBetweenDates(date_start: string, date_until: string, uids?: number[]) {

    let params = new HttpParams()

    if (uids) {
      for (let uid of uids) {
        params = params.append('ids[]', uid.toString())
      }
    }

    params = params.append('date_start', date_start)
    params = params.append('date_until', date_until)

    const path = environment.apiURL + 'user_ids_with_operations_between_dates'

    return this.http.get<number[]>(path, {params})
  }

  /**
   * Lädt alle User mit ADMINISTRATOR-Rolle für E-Mail-Benachrichtigungen
   */
  public getAdminUsers() {
    const path = environment.apiURL + 'users/admin_users'
    return this.http.get<UserEmailHash[]>(path)
  }

}
